import React, { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../services/api";
import { useAiJobState } from "../hooks/useAiJobState";
import {
  beginOptimisticEnrichmentQueue,
  finalizeOptimisticEnrichmentQueue,
  finalizeOptimisticEnrichmentQueueOnTimeout,
  isEnrichmentQueueTimeoutError,
  rollbackOptimisticEnrichmentQueue,
} from "../utils/enrichmentClientCache";
import { createDevConsole } from "../utils/devLogger";

const console = createDevConsole();

const S3ConnectorImport = ({
  onBatchProcessingStarted,
  onError,
  onClearError,
  trialStatus = null,
  knowledgeAnchor = null,
  anchorApplicationId = null,
}) => {
  const { setAiProcessing } = useAiJobState();
  const [connectors, setConnectors] = useState([]);
  const [loadingConnectors, setLoadingConnectors] = useState(true);
  const [selectedConnectorId, setSelectedConnectorId] = useState("");
  const [path, setPath] = useState("");
  const [pathHistory, setPathHistory] = useState([]); // stack of path segments for breadcrumb
  const [containers, setContainers] = useState([]);
  const [items, setItems] = useState([]);
  const [nextToken, setNextToken] = useState(null);
  const [loadingBrowse, setLoadingBrowse] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [selectedContainerPaths, setSelectedContainerPaths] = useState(new Set());
  const [processing, setProcessing] = useState(false);
  const [addConnectorOpen, setAddConnectorOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    name: "",
    bucket: "",
    region: "us-east-1",
    prefix: "",
    aws_access_key_id: "",
    aws_secret_access_key: "",
  });
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [editingConnectorId, setEditingConnectorId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    bucket: "",
    region: "us-east-1",
    prefix: "",
    aws_access_key_id: "",
    aws_secret_access_key: "",
  });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [editSuccessMessage, setEditSuccessMessage] = useState("");

  const fetchConnectors = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!silent) setLoadingConnectors(true);
    try {
      const res = await apiFetch("/api/connectors", { method: "GET" });
      if (res && res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setConnectors(list);
        const ids = list.map((c) => c.id);
        setSelectedConnectorId((current) => {
          if (!current || !ids.includes(current)) return list[0]?.id || "";
          return current;
        });
      }
    } catch (e) {
      console.error("Failed to fetch connectors", e);
      onError?.("Failed to load connectors.");
    } finally {
      setLoadingConnectors(false);
    }
  }, [onError]);

  // Load connector list on mount
  useEffect(() => {
    fetchConnectors();
  }, []);

  // Refetch when user returns to this browser tab so saved connectors (e.g. from another session) appear
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") fetchConnectors({ silent: true });
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [fetchConnectors]);

  const browse = useCallback(
    async (connectorId, pathVal = "", token = null, append = false) => {
      if (!connectorId) return;
      if (!append) setLoadingBrowse(true);
      try {
        const params = new URLSearchParams();
        if (pathVal) params.set("path", pathVal);
        if (token) params.set("nextToken", token);
        const url = `/api/connectors/${connectorId}/browse${params.toString() ? `?${params.toString()}` : ""}`;
        const res = await apiFetch(url, { method: "GET" });
        if (!res || !res.ok) {
          let msg = "Failed to browse. Check connector credentials.";
          try {
            const errBody = await res.json();
            if (errBody?.detail) msg = errBody.detail;
          } catch (_) {}
          onError?.(msg);
          return;
        }
        onClearError?.();
        const data = await res.json();
        if (append) {
          setItems((prev) => [...prev, ...(data.items || [])]);
        } else {
          setContainers(data.containers || []);
          setItems(data.items || []);
        }
        setNextToken(data.nextToken || null);
        setPath(data.path || pathVal);
      } catch (e) {
        console.error("Browse failed", e);
        onError?.(e.message || "Failed to browse.");
      } finally {
        setLoadingBrowse(false);
      }
    },
    [onError, onClearError]
  );

  useEffect(() => {
    if (!selectedConnectorId) return;
    onClearError?.();
    setPath("");
    setPathHistory([]);
    setContainers([]);
    setItems([]);
    setNextToken(null);
    browse(selectedConnectorId, "", null);
  }, [selectedConnectorId, browse, onClearError]);

  const navigateTo = (containerPath) => {
    setPathHistory((prev) => [...prev, path]);
    setPath(containerPath);
    browse(selectedConnectorId, containerPath);
  };

  const breadcrumbBack = () => {
    if (pathHistory.length === 0) {
      setPath("");
      browse(selectedConnectorId, "");
      return;
    }
    const newHistory = pathHistory.slice(0, -1);
    const newPath = newHistory[newHistory.length - 1] || "";
    setPathHistory(newHistory);
    setPath(newPath);
    browse(selectedConnectorId, newPath);
  };

  const toggleItem = (id) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleContainer = (containerPath) => {
    setSelectedContainerPaths((prev) => {
      const next = new Set(prev);
      if (next.has(containerPath)) next.delete(containerPath);
      else next.add(containerPath);
      return next;
    });
  };

  const selectAllOnPage = () => {
    const allItemIds = new Set(items.map((i) => i.id));
    const allSelected =
      items.length > 0 && items.every((i) => selectedItemIds.has(i.id));
    if (allSelected) {
      setSelectedItemIds((prev) => {
        const next = new Set(prev);
        allItemIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedItemIds((prev) => new Set([...prev, ...allItemIds]));
    }
  };

  const handleProcessSelected = async () => {
    const itemIds = [...selectedItemIds];
    const containerPaths = [...selectedContainerPaths];
    if (itemIds.length === 0 && containerPaths.length === 0) {
      onError?.("Select at least one file or folder.");
      return;
    }

    setProcessing(true);
    let provisionalId = null;
    const anchorId = String(
      knowledgeAnchor?.id || anchorApplicationId || ""
    ).trim();
    const effectiveAnchor = knowledgeAnchor || {
      scopeType: "entity",
      id: anchorId,
      extractMode: "auto",
    };
    try {
      const connector = connectors.find((c) => c.id === selectedConnectorId);
      const connectorName = connector?.name || "S3";
      const processPath = `/api/connectors/${selectedConnectorId}/process-enrichment`;

      provisionalId = beginOptimisticEnrichmentQueue({
        anchorApplicationId: anchorId,
      });
      setAiProcessing(true);

      const res = await apiFetch(processPath, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          itemIds: itemIds.length ? itemIds : undefined,
          containerPaths: containerPaths.length ? containerPaths : undefined,
          sourceLabel: `Add knowledge — ${connectorName} import`,
          anchor_application_id: anchorId,
          anchor_kind: effectiveAnchor.scopeType || "entity",
          extract_mode: effectiveAnchor.extractMode || "auto",
        }),
        timeout: 120000,
      });
      if (!res || !res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Process request failed.");
      }
      const data = await res.json();
      const jobId = data.jobId;
      if (jobId) {
        try {
          finalizeOptimisticEnrichmentQueue(
            provisionalId,
            { job_id: jobId, job_display_name: data.job_display_name },
            { anchorApplicationId: anchorId }
          );
          setAiProcessing(true);
        } catch (_) {}

        onBatchProcessingStarted?.(data);
      } else {
        rollbackOptimisticEnrichmentQueue(provisionalId);
        onError?.("Process started but no job id was returned.");
      }
      setSelectedItemIds(new Set());
      setSelectedContainerPaths(new Set());
    } catch (e) {
      if (isEnrichmentQueueTimeoutError(e)) {
        const timeoutJobId = finalizeOptimisticEnrichmentQueueOnTimeout(provisionalId, {
          anchorApplicationId: anchorId,
        });
        setAiProcessing(true);
        onBatchProcessingStarted?.({ jobId: timeoutJobId, timedOut: true });
        setSelectedItemIds(new Set());
        setSelectedContainerPaths(new Set());
        return;
      }
      rollbackOptimisticEnrichmentQueue(provisionalId);
      onError?.(e.message || "Failed to start processing.");
    } finally {
      setProcessing(false);
    }
  };

  const handleAddConnector = async (e) => {
    e.preventDefault();
    if (!addForm.name?.trim() || !addForm.bucket?.trim() || !addForm.region?.trim()) {
      onError?.("Name, bucket, and region are required.");
      return;
    }
    if (!addForm.aws_access_key_id?.trim() || !addForm.aws_secret_access_key?.trim()) {
      onError?.("Access Key ID and Secret Access Key are required.");
      return;
    }
    setAddSubmitting(true);
    try {
      const res = await apiFetch("/api/connectors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: addForm.name.trim(),
          type: "s3",
          config: {
            bucket: addForm.bucket.trim(),
            region: addForm.region.trim(),
            prefix: (addForm.prefix || "").trim().replace(/\/+$/, ""),
          },
          credentials: {
            aws_access_key_id: addForm.aws_access_key_id.trim(),
            aws_secret_access_key: addForm.aws_secret_access_key.trim(),
          },
        }),
      });
      if (!res || !res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to create connector.");
      }
      setAddConnectorOpen(false);
      setAddForm({
        name: "",
        bucket: "",
        region: "us-east-1",
        prefix: "",
        aws_access_key_id: "",
        aws_secret_access_key: "",
      });
      await fetchConnectors();
    } catch (e) {
      onError?.(e.message || "Failed to create connector.");
    } finally {
      setAddSubmitting(false);
    }
  };

  const openEditConnector = (connector) => {
    if (!connector) return;
    setEditForm({
      name: connector.name || "",
      bucket: (connector.config && connector.config.bucket) || "",
      region: (connector.config && connector.config.region) || "us-east-1",
      prefix: (connector.config && connector.config.prefix) || "",
      aws_access_key_id: "",
      aws_secret_access_key: "",
    });
    setEditingConnectorId(connector.id);
  };

  const handleEditConnector = async (e) => {
    e.preventDefault();
    const connectorId = editingConnectorId;
    if (!connectorId) return;
    setEditSubmitting(true);
    onClearError?.();
    try {
      const body = {
        name: editForm.name.trim(),
        config: {
          bucket: editForm.bucket.trim(),
          region: editForm.region.trim(),
          prefix: (editForm.prefix || "").trim().replace(/\/+$/, ""),
        },
      };
      const hasNewCredentials =
        editForm.aws_access_key_id.trim() && editForm.aws_secret_access_key;
      if (hasNewCredentials) {
        body.credentials = {
          aws_access_key_id: editForm.aws_access_key_id.trim(),
          aws_secret_access_key: editForm.aws_secret_access_key,
        };
      }
      const res = await apiFetch(`/api/connectors/${connectorId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res || !res.ok) {
        const err = await res.json().catch(() => ({}));
        let msg = "Failed to update connector.";
        if (typeof err.detail === "string") msg = err.detail;
        else if (Array.isArray(err.detail) && err.detail[0]?.msg) msg = err.detail.map((d) => d.msg).join("; ");
        throw new Error(msg);
      }
      setEditSuccessMessage("Connector updated.");
      setEditingConnectorId(null);
      await fetchConnectors({ silent: true });
      if (hasNewCredentials) {
        await browse(connectorId, "", null);
      }
      setTimeout(() => setEditSuccessMessage(""), 2500);
    } catch (e) {
      onError?.(e.message || "Failed to update connector.");
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteConnector = async (connectorId) => {
    if (!connectorId) return;
    setDeleteSubmitting(true);
    setDeleteConfirmId(null);
    try {
      const res = await apiFetch(`/api/connectors/${connectorId}`, {
        method: "DELETE",
      });
      if (!res || !res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to delete connector.");
      }
      if (selectedConnectorId === connectorId) {
        setSelectedConnectorId("");
        setContainers([]);
        setItems([]);
        setPath("");
        setPathHistory([]);
        setNextToken(null);
      }
      await fetchConnectors({ silent: true });
    } catch (e) {
      onError?.(e.message || "Failed to delete connector.");
      await fetchConnectors({ silent: true });
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const totalSelected =
    selectedItemIds.size +
    selectedContainerPaths.size;
  const breadcrumbParts = path ? path.replace(/\/$/, "").split("/").filter(Boolean) : [];

  if (loadingConnectors) {
    return (
      <div className="text-gray-400 text-sm p-4">
        Loading connectors…
      </div>
    );
  }

  if (connectors.length === 0 && !addConnectorOpen) {
    return (
      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
        <p className="text-gray-300 text-sm mb-3">
          No S3 connectors configured. Add one to browse and import files from your bucket.
        </p>
        <button
          type="button"
          onClick={() => setAddConnectorOpen(true)}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
        >
          Add S3 connector
        </button>
        {addConnectorOpen && (
          <form onSubmit={handleAddConnector} className="mt-4 space-y-3 max-w-md">
            <input
              type="text"
              placeholder="Connector name"
              value={addForm.name}
              onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              required
            />
            <input
              type="text"
              placeholder="Bucket"
              value={addForm.bucket}
              onChange={(e) => setAddForm((f) => ({ ...f, bucket: e.target.value }))}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              required
            />
            <input
              type="text"
              placeholder="Region (e.g. us-east-1)"
              value={addForm.region}
              onChange={(e) => setAddForm((f) => ({ ...f, region: e.target.value }))}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              required
            />
            <input
              type="text"
              placeholder="Prefix (optional)"
              value={addForm.prefix}
              onChange={(e) => setAddForm((f) => ({ ...f, prefix: e.target.value }))}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
            />
            <input
              type="text"
              placeholder="Access Key ID"
              value={addForm.aws_access_key_id}
              onChange={(e) => setAddForm((f) => ({ ...f, aws_access_key_id: e.target.value }))}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              autoComplete="off"
              required
            />
            <input
              type="password"
              placeholder="Secret Access Key"
              value={addForm.aws_secret_access_key}
              onChange={(e) => setAddForm((f) => ({ ...f, aws_secret_access_key: e.target.value }))}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white text-sm"
              autoComplete="new-password"
              required
            />
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={addSubmitting}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {addSubmitting ? "Saving…" : "Save connector"}
              </button>
              <button
                type="button"
                onClick={() => setAddConnectorOpen(false)}
                className="px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    );
  }

  const selectedConnector = connectors.find((c) => c.id === selectedConnectorId);

  return (
    <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 space-y-4">
      {editSuccessMessage && (
        <div className="p-2 rounded bg-green-900/30 border border-green-600 text-green-300 text-sm">
          {editSuccessMessage}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-gray-300 text-sm">Connector:</label>
        <select
          key={connectors.map((c) => c.id).join(",")}
          value={selectedConnectorId}
          onChange={(e) => setSelectedConnectorId(e.target.value)}
          className="px-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-white text-sm"
        >
          {connectors.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.type})
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setAddConnectorOpen(true)}
          className="text-sm text-blue-400 hover:text-blue-300"
        >
          + Add connector
        </button>
        {selectedConnector && (
          <>
            <button
              type="button"
              onClick={() => openEditConnector(selectedConnector)}
              className="text-sm text-amber-400 hover:text-amber-300"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => setDeleteConfirmId(selectedConnector.id)}
              className="text-sm text-red-400 hover:text-red-300"
            >
              Delete
            </button>
          </>
        )}
      </div>
      <p className="text-gray-500 text-xs">
        Credentials are stored securely in the backend using Google Cloud Secret Manager.
      </p>

      {deleteConfirmId && (
        <div className="p-3 bg-gray-700/50 rounded border border-gray-600 flex flex-wrap items-center gap-2">
          <span className="text-gray-300 text-sm">
            Delete &quot;{connectors.find((c) => c.id === deleteConfirmId)?.name || "this connector"}&quot;? This cannot be undone.
          </span>
          <button
            type="button"
            onClick={() => handleDeleteConnector(deleteConfirmId)}
            disabled={deleteSubmitting}
            className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
          >
            {deleteSubmitting ? "Deleting…" : "Delete"}
          </button>
          <button
            type="button"
            onClick={() => setDeleteConfirmId(null)}
            disabled={deleteSubmitting}
            className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      )}

      {editingConnectorId && (
        <form onSubmit={handleEditConnector} className="p-3 bg-gray-700/50 rounded space-y-2 max-w-md border border-gray-600">
          <h4 className="text-gray-200 text-sm font-medium">Edit connector</h4>
          <input
            type="text"
            placeholder="Name"
            value={editForm.name}
            onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
            className="w-full px-2 py-1.5 bg-gray-700 border rounded text-white text-sm"
            required
          />
          <input
            type="text"
            placeholder="Bucket"
            value={editForm.bucket}
            onChange={(e) => setEditForm((f) => ({ ...f, bucket: e.target.value }))}
            className="w-full px-2 py-1.5 bg-gray-700 border rounded text-white text-sm"
            required
          />
          <input
            type="text"
            placeholder="Region"
            value={editForm.region}
            onChange={(e) => setEditForm((f) => ({ ...f, region: e.target.value }))}
            className="w-full px-2 py-1.5 bg-gray-700 border rounded text-white text-sm"
            required
          />
          <input
            type="text"
            placeholder="Prefix (optional)"
            value={editForm.prefix}
            onChange={(e) => setEditForm((f) => ({ ...f, prefix: e.target.value }))}
            className="w-full px-2 py-1.5 bg-gray-700 border rounded text-white text-sm"
          />
          <p className="text-gray-500 text-xs">Leave credentials blank to keep existing.</p>
          <input
            type="text"
            placeholder="Access Key ID (optional)"
            value={editForm.aws_access_key_id}
            onChange={(e) => setEditForm((f) => ({ ...f, aws_access_key_id: e.target.value }))}
            className="w-full px-2 py-1.5 bg-gray-700 border rounded text-white text-sm"
            autoComplete="off"
          />
          <input
            type="password"
            placeholder="Secret Access Key (optional)"
            value={editForm.aws_secret_access_key}
            onChange={(e) => setEditForm((f) => ({ ...f, aws_secret_access_key: e.target.value }))}
            className="w-full px-2 py-1.5 bg-gray-700 border rounded text-white text-sm"
            autoComplete="new-password"
          />
          <div className="flex gap-2">
            <button type="submit" disabled={editSubmitting} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-70 disabled:cursor-wait" aria-busy={editSubmitting}>
              {editSubmitting ? "Saving…" : "Save"}
            </button>
            <button type="button" onClick={() => setEditingConnectorId(null)} className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded hover:bg-gray-700">
              Cancel
            </button>
          </div>
        </form>
      )}

      {addConnectorOpen && (
        <form onSubmit={handleAddConnector} className="p-3 bg-gray-700/50 rounded space-y-2 max-w-md">
          <input type="text" placeholder="Name" value={addForm.name} onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))} className="w-full px-2 py-1.5 bg-gray-700 border rounded text-white text-sm" required />
          <input type="text" placeholder="Bucket" value={addForm.bucket} onChange={(e) => setAddForm((f) => ({ ...f, bucket: e.target.value }))} className="w-full px-2 py-1.5 bg-gray-700 border rounded text-white text-sm" required />
          <input type="text" placeholder="Region" value={addForm.region} onChange={(e) => setAddForm((f) => ({ ...f, region: e.target.value }))} className="w-full px-2 py-1.5 bg-gray-700 border rounded text-white text-sm" required />
          <input type="text" placeholder="Prefix (optional)" value={addForm.prefix} onChange={(e) => setAddForm((f) => ({ ...f, prefix: e.target.value }))} className="w-full px-2 py-1.5 bg-gray-700 border rounded text-white text-sm" />
          <input type="text" placeholder="Access Key ID" value={addForm.aws_access_key_id} onChange={(e) => setAddForm((f) => ({ ...f, aws_access_key_id: e.target.value }))} className="w-full px-2 py-1.5 bg-gray-700 border rounded text-white text-sm" autoComplete="off" required />
          <input type="password" placeholder="Secret Access Key" value={addForm.aws_secret_access_key} onChange={(e) => setAddForm((f) => ({ ...f, aws_secret_access_key: e.target.value }))} className="w-full px-2 py-1.5 bg-gray-700 border rounded text-white text-sm" autoComplete="new-password" required />
          <div className="flex gap-2">
            <button type="submit" disabled={addSubmitting} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded">Save</button>
            <button type="button" onClick={() => setAddConnectorOpen(false)} className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded">Cancel</button>
          </div>
        </form>
      )}

      {/* Breadcrumb */}
      <div className="flex items-center gap-1 text-sm text-gray-400 flex-wrap">
        <button
          type="button"
          onClick={breadcrumbBack}
          className="hover:text-white"
        >
          {pathHistory.length > 0 ? "← Back" : "📁 Root"}
        </button>
        {breadcrumbParts.map((seg, i) => (
          <span key={i}>
            <span className="text-gray-500">/</span>
            <span className="text-gray-300">{seg}</span>
          </span>
        ))}
      </div>

      {loadingBrowse ? (
        <p className="text-gray-400 text-sm">Loading…</p>
      ) : (
        <>
          <div className="flex items-center gap-2 text-sm text-gray-300">
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={items.length > 0 && items.every((i) => selectedItemIds.has(i.id))}
                onChange={selectAllOnPage}
              />
              Select all on this page
            </label>
          </div>

          <div className="space-y-1 max-h-64 overflow-y-auto">
            {containers.map((c) => (
              <div
                key={c.id}
                className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-700/50"
              >
                <input
                  type="checkbox"
                  checked={selectedContainerPaths.has(c.path)}
                  onChange={() => toggleContainer(c.path)}
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  type="button"
                  onClick={() => navigateTo(c.path)}
                  className="text-left text-blue-300 hover:text-blue-200 flex-1 flex items-center gap-2"
                >
                  📁 {c.name}
                </button>
              </div>
            ))}
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-700/50"
              >
                <input
                  type="checkbox"
                  checked={selectedItemIds.has(item.id)}
                  onChange={() => toggleItem(item.id)}
                />
                <span className="text-gray-300 text-sm flex-1">
                  📄 {item.name}
                </span>
                <span className="text-gray-500 text-xs">
                  {item.size != null ? `${(item.size / 1024).toFixed(1)} KB` : ""}
                </span>
              </div>
            ))}
          </div>

          {nextToken && (
            <button
              type="button"
              onClick={() => browse(selectedConnectorId, path, nextToken, true)}
              className="text-sm text-blue-400 hover:text-blue-300"
            >
              Load more…
            </button>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-gray-700">
            <span className="text-gray-400 text-sm">
              {totalSelected > 0
                ? `${selectedItemIds.size} file(s), ${selectedContainerPaths.size} folder(s) selected`
                : "Select files or folders to process"}
            </span>
            <button
              type="button"
              onClick={handleProcessSelected}
              disabled={totalSelected === 0 || processing}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processing ? "Queuing enrichment…" : "Queue enrichment"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default S3ConnectorImport;
